import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { translate } from 'components/Translation';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import Button from 'components/CustomButtons/Button';
import TabsWithDots from 'components/TabsWithDots/TabsWithDots';
import BillsTab from './subcomponents/BillsTab';
import styles from 'assets/jss/material-dashboard-pro-react/views/payments/payAnyBillStyle';
import { makeStyles } from '@material-ui/core';
import { UnpaidBills } from 'utils/api/models/bills/unpaidBills';
import { PaymentService } from 'services/payments/payment';
import prepareData from './helpers/prepare';
import { finalize } from 'rxjs/operators';
import { useDispatch } from 'react-redux';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { useHistory } from 'react-router';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { clearBillValue, setBillValue } from 'redux/modules/bills/actions';
import { BillsService } from 'services/bills/bills';
import { SingleBillId } from 'utils/api/models/bills/singleBillId';
import { AccountsService } from 'services/accounts/accounts';
import { IPaymentToken } from 'utils/api/models/payments/paymentTokens';
import { AccountType, BillingType } from 'utils/api/models/accounts/singleAccount';
import { setPaymentType } from 'redux/modules/paymentType/actions';
import Validator from 'card-validator';
import { PAY_ANY_BILL } from '../constants';
import PaymentMethod, { PaymentStatus, PaymentStatusType } from 'components/PaymentMethod';
import PaymentConfirmedDialog from '../common/PaymentConfirmedDialog';
import PaymentRejectedDialog from '../common/PaymentRejectedDialog';
import { PaymentRedirection } from 'components/PaymentMethod/helper/payment';
import { loadQualtrics } from '../../../utils/qualtrics';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import useNewTheme from 'commons/hooks/newtheme/useNewTheme';

// @ts-ignore
const useStyles = makeStyles(styles);

const steps = {
	BILLS: 0,
	PAYMENT: 1,
};

type dialogType = 'paymentConfirmed' | 'paymentRejected' | null;

interface ISingleButton {
	color: types.theme.buttonColor;
	text: string;
	onClick: () => void;
	disabled?: boolean;
	id?: string;
}

interface IPayAnyBillProps {
	location: {
		search: string;
	};
}

const PayAnyBill = ({ location }: IPayAnyBillProps) => {
	const [activeTab, setActiveTab] = useState(steps.BILLS);
	const [formData, setFormData] = useState<types.payment.IFormData>({
		number: '',
		cvv: '',
		date: '',
		holder: '',
		email: '',
		type: 'card',
		cardType: 'VISA',
		tokenId: '',
		saveCard: false,
	});
	const [validation, setValidation] = useState<types.payment.IValidation>({
		number: { isValid: true, isPotentiallyValid: true, card: null },
		cvv: { isValid: true, isPotentiallyValid: true },
		date: { isValid: true, isPotentiallyValid: true },
		email: { isValid: true, isPotentiallyValid: true },
	});
	const [unpaidBills, setUnpaidBills] = useState<UnpaidBills | null>(null);
	const [selectedBills, setSelectedBills] = useState<number[]>([]);
	const [computedAccountType, setComputedAccountType] = useState<string>('');
	const [paymentLimitReached, setPaymentLimitReached] = useState<types.payment.paymentLimitReached>(null);
	const [paymentError, setPaymentError] = useState<string | null>(null);
	const [showDialog, setShowDialog] = useState<dialogType>(null);
	const [showPaymentFrame, setShowPaymentFrame] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const [showResults, setShowResults] = useState<boolean>(false);
	const [billingId, setBillingId] = useState<string>('');
	const [billingAccountNo, setBillingAccountNo] = useState<string>('');
	const [accountType, setAccountType] = useState<string>('');
	const [checkValidity, setCheckValidity] = useState<boolean>(false);
	const [searchValue, setSearchValue] = useState<{ [key in types.bills.searchType]: string }>({
		mobileNumber: '',
		billingAccountNo: '',
		billNo: '',
	});
	const [billingIds, setBillingIds] = useState<SingleBillId[]>([]);
	const [paymentResult, setPaymentResult] = useState<{ status: PaymentStatusType | undefined; refNumber: string }>({
		status: undefined,
		refNumber: '',
	});
	const [radioType, setRadioType] = useState<types.bills.searchType>('mobileNumber');
	const dispatch = useDispatch();
	const classes = useStyles();
	const history = useHistory();
	const isMounted = useRef(true);
	const bills: types.bills.IExtendedBill[] = useMemo(() => {
		if (unpaidBills) {
			switch (radioType) {
				case 'mobileNumber':
					return unpaidBills.billsData
						.map((billData) =>
							billData.bills.map((singleBill) => ({
								...singleBill,
								disabled: singleBill.unpaidAmount <= 0,
								billingId: billData.billingId,
							}))
						)
						.flat();
				case 'billingAccountNo':
					return unpaidBills.billsData
						.filter((billData) => String(billData.billingId) === searchValue.billingAccountNo)
						.map((billData) =>
							billData.bills.map((singleBill) => ({
								...singleBill,
								disabled: singleBill.unpaidAmount <= 0,
								billingId: billData.billingId,
							}))
						)
						.flat();
				case 'billNo':
					return unpaidBills.billsData
						.map((billData) =>
							billData.bills.map((singleBill) => ({
								...singleBill,
								disabled: singleBill.unpaidAmount <= 0,
								billingId: billData.billingId,
							}))
						)
						.flat()
						.filter((singleBill) => String(singleBill.billNo) === searchValue.billNo);
			}
		}
		return [];
	}, [unpaidBills, radioType, searchValue]);

	const filteredBills = useMemo(() => {
		return bills.filter((bill) => selectedBills.indexOf(bill.billNo) >= 0);
	}, [bills, selectedBills]);

	const totalAmount: number = useMemo(() => {
		return filteredBills.reduce((acc, bill) => {
			return acc + bill.unpaidAmount;
		}, 0);
	}, [filteredBills]);

	const payEnabled = useMemo(() => {
		const addressFieldsFilled = formData?.email.length > 0;
		const cardFieldsFilled =
			Object.keys(validation).every((key) => validation[key].isPotentiallyValid) &&
			['number', 'cvv', 'holder'].every((key) => formData[key].length > 0);
		const token = formData.type === 'token';
		return (
			addressFieldsFilled &&
			(token || (cardFieldsFilled && Validator.expirationDate(formData.date) && formData.date?.length === 4))
		);
	}, [formData, validation]);

	const nextStepDisabled = useMemo(() => {
		if (activeTab === steps.BILLS) {
			return selectedBills.length === 0;
		}
		return !payEnabled;
	}, [activeTab, selectedBills, payEnabled]);

	const computedBillingId: string = useMemo(() => {
		if (radioType === 'mobileNumber') {
			return billingId;
		}
		if (filteredBills.length > 0) {
			return String(filteredBills[0].billingId);
		}
		return '';
	}, [radioType, billingId, filteredBills]);

	const isValid = useCallback(() => {
		return Object.keys(validation).every((key) => validation[key].isPotentiallyValid && validation[key]?.isValid);
	}, [validation]);

	const getAccountType = useCallback(
		async (billingId: string): Promise<string> => {
			return new Promise((resolve, reject) => {
				const singleBillingId = billingIds.find((singleBillingId) => singleBillingId.billingId === billingId);
				if (singleBillingId) {
					const { accountId } = singleBillingId;
					dispatch(showLoadingIndicator());
					AccountsService.getBillingAccountInformation({
						billingId,
						accountId,
					})
						.pipe(finalize(() => dispatch(hideLoadingIndicator())))
						.subscribe(
							({ accountType }) => {
								resolve(accountType);
							},
							() => reject()
						);
				} else {
					resolve('');
				}
			});
		},
		[billingIds, dispatch]
	);

	const pay = useCallback(async () => {
		dispatch(showLoadingIndicator());
		dispatch(
			setBillValue({
				billValue: totalAmount,
			})
		);
		const computedAccountType =
			radioType === 'mobileNumber'
				? accountType
				: radioType === 'billingAccountNo'
				? AccountType.MOBILE
				: await getAccountType(computedBillingId);
		const data: any = prepareData(validation, formData, filteredBills, computedBillingId, computedAccountType);
		PaymentService.makePayment(data)
			.pipe(finalize(() => dispatch(hideLoadingIndicator())))
			.subscribe(
				() => {
					showPaymentConfirmedDialog();
				},
				(error) => {
					switch (error.response.code) {
						case 'PAYER_AUTHENTICATION_REQUIRED':
							dispatch(
								setPaymentType({
									action: 'OTHER',
								})
							);
							PaymentRedirection(error?.response?.url);
							break;

						case 'CREDIT_CARD_PAYMENT_REQUEST_EXCEEDS_MAX_DONATION_AMOUNT':
							setPaymentLimitReached('credit');
							break;

						case 'DEBIT_CARD_PAYMENT_REQUEST_EXCEEDS_MAX_TOPUP_AMOUNT':
							setPaymentLimitReached('debit');
							break;

						case 'TRANSACTION_REJECTED':
							showPaymentRejectedDialog();
							break;

						default:
							setPaymentError(error.response.details || translate('error.invalid.request'));
							break;
					}
				}
			);
	}, [
		formData,
		validation,
		dispatch,
		filteredBills,
		totalAmount,
		computedBillingId,
		radioType,
		getAccountType,
		accountType,
	]);

	const organizationBillingIds: string[] = useMemo(() => {
		return billingIds.map((singleBillingId) => singleBillingId.billingId);
	}, [billingIds]);

	const nextStepAction = useCallback(() => {
		if (activeTab === steps.BILLS) {
			setActiveTab(steps.PAYMENT);
		}
		if (activeTab === steps.PAYMENT) {
			setCheckValidity(true);
			(isValid() || payEnabled) && pay();
		}
	}, [activeTab, pay, isValid, payEnabled]);

	const back = () => {
		setActiveTab(steps.BILLS);
	};

	const hideResults = () => {
		setShowResults(false);
	};

	const buttons = useMemo(() => {
		return [
			activeTab === steps.PAYMENT && {
				text: translate('back'),
				onClick: back,
				color: 'tertiary-light',
			},
			{
				text: translate('pay'),
				onClick: nextStepAction,
				color: 'primary-light',
				disabled: nextStepDisabled,
				id: 'next-button',
			},
		].filter(Boolean) as ISingleButton[];
	}, [activeTab, nextStepDisabled, nextStepAction]);

	const setFormSingleField: types.payment.setFormData = (name: keyof types.payment.IFormData, value: string) =>
		setFormData((formData) => ({ ...formData, [name]: value }));

	const hideDialog = () => {
		loadQualtrics({
			activity_type: `Pay Any Bills - ${showDialog === 'paymentConfirmed' ? 'Success' : 'Failure'}`,
		});
		setShowDialog(null);
	};

	const showPaymentConfirmedDialog = () => {
		setShowDialog('paymentConfirmed');
	};

	const toggleSaveCard = () => setFormData((formData) => ({ ...formData, saveCard: !formData.saveCard }));

	const showPaymentRejectedDialog = () => {
		setShowDialog('paymentRejected');
	};

	const hidePaymentConfirmedDialog = () => {
		hideDialog();
		dispatch(clearBillValue());
		history.push('/dashboard/payments/bills');
	};

	const setSaveCard = (saveCard: boolean) => setFormData((formData) => ({ ...formData, saveCard }));

	const fetchBills = useCallback((billingId?: string) => {
		setLoading(true);
		BillsService.getCustomerUnpaidBillsForAccounts(billingId)
			.pipe(
				finalize(() => {
					if (isMounted.current) {
						setLoading(false);
						setShowResults(true);
					}
				})
			)
			.subscribe((bills) => {
				if (isMounted.current) {
					setUnpaidBills(bills);
				}
			});
	}, []);

	useEffect(() => {
		BillsService.getCorpBillingIds().subscribe(({ billingIds }) => isMounted.current && setBillingIds(billingIds));
	}, []);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const setTokenData = useCallback(
		(token: IPaymentToken) =>
			setFormData((formData) => ({
				...formData,
				number: token.cardNumber,
				cardType: token.cardType,
				holder: `${token.firstName} ${token.lastName}`,
				tokenId: token.id,
				type: 'token',
			})),
		[setFormData]
	);

	useEffect(() => {
		const subscribe = async () => {
			const _accountType = await getAccountType(computedBillingId);
			setComputedAccountType(
				radioType === 'mobileNumber'
					? accountType
					: radioType === 'billingAccountNo'
					? AccountType.MOBILE
					: _accountType
			);
		};
		subscribe();
	}, [getAccountType, radioType, computedBillingId]);

	const billItems = useMemo(() => {
		if (!unpaidBills || !selectedBills) return [];

		const selectedBillSet = new Set(selectedBills);

		return unpaidBills.billsData.flatMap((unPaidBill) => {
			return unPaidBill.bills
				.filter((bill) => selectedBillSet.has(bill.billNo))
				.map((bill) => ({
					accountId: unPaidBill.billingId.toString(),
					accountType: computedAccountType,
					billingType: BillingType.POSTPAID,
					code: bill.billNo.toString(),
					name: '',
					quantity: '1',
					type: 'BILL',
					unitPrice: bill.unpaidAmount,
				}));
		});
	}, [unpaidBills, selectedBills, computedAccountType]);

	const onPaymentResult = (result: PaymentStatusType, response: any) => {
		setPaymentResult({
			status: result,
			refNumber: response?.transactionReferenceNumber ?? response?.ticketNumber ?? '',
		});
	};

	const isNewDashboardEnabled = useNewTheme();

	if (paymentResult.status) {
		return <PaymentStatus status={paymentResult.status} RefNumber={paymentResult.refNumber} />;
	}

	return (
		<>
			{isNewDashboardEnabled && (
				<BreadcrumbsItem to="/payments/pay-any-bill">
					{`${translate('path.my.payments')} - ${translate('path.pay.any.bill')}`}
				</BreadcrumbsItem>
			)}

			<GridContainer>
				<GridItem xs={12}>
					<div>
						<TabsWithDots
							tabs={[
								{
									name: translate('bills'),
									title: translate('bills.look.for.bills'),
									content: (
										<BillsTab
											selectedBills={selectedBills}
											setSelectedBills={setSelectedBills}
											bills={bills}
											loadingUnpaidBills={loading}
											hideResults={hideResults}
											showResults={showResults}
											setBillingId={setBillingId}
											radioType={radioType}
											setRadioType={setRadioType}
											searchValue={searchValue}
											setSearchValue={setSearchValue}
											billingAccountNo={billingAccountNo}
											setBillingAccountNo={setBillingAccountNo}
											setAccountType={setAccountType}
											fetchBills={fetchBills}
											organizationBillingIds={organizationBillingIds}
										/>
									),
								},
								{
									name: translate('bills.payment'),
									content: (
										<PaymentMethod
											bills={billItems}
											onPaymentResult={onPaymentResult}
											referrer={PAY_ANY_BILL}
										/>
									),
								},
							]}
							contentClasses={activeTab === steps.BILLS ? classes.contentWrapper : ''}
							activeTab={activeTab}
							setActiveTab={setActiveTab}
							footer={
								<ConditionalRender
									show={activeTab === steps.BILLS}
									onTrue={() => (
										<div className={classes.footerWrapper}>
											{buttons.map((button, index) => (
												<Button
													key={index}
													color={button.color as types.theme.buttonColor}
													onClick={button.onClick}
													disabled={button.disabled}
													id={button.id}
												>
													{button.text}
												</Button>
											))}
										</div>
									)}
								/>
							}
						/>
					</div>
					<PaymentConfirmedDialog
						isOpen={showDialog === 'paymentConfirmed'}
						hideDialog={hidePaymentConfirmedDialog}
					/>
					<PaymentRejectedDialog isOpen={showDialog === 'paymentRejected'} hideDialog={hideDialog} />
				</GridItem>
			</GridContainer>
		</>
	);
};

export default PayAnyBill;
